import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import axios from "axios";
import Cookies from "js-cookie";
import ModalLoadingOverlay, {
  modalLoadingContainerSx,
} from "./ModalLoadingOverlay";

export default function AddLogActivity({
  open,
  onClose,
  candidateId,
  onActivityAdded,
  regardingOptions,
  activityOptions,
}) {
  const [regarding, setRegarding] = useState("");
  const [activity, setActivity] = useState("");
  const [activityDesc, setActivityDesc] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const isSaveDisabled = !regarding || !activity || !activityDesc.trim();

  const handleClose = () => {
    setRegarding("");
    setActivity("");
    setActivityDesc("");
    onClose();
  };

  const addLogActivity = async () => {
    try {
      setLoading(true);
      const res = await axios.post(
        `/add-candidate-activity`,
        {
          candidate_id: candidateId,
          joborder_id: regarding,
          type: activity,
          notes: activityDesc,
        },
        {
          headers: {
            authorization:
              Cookies.get("_secure_ARJ_") || Cookies.get("_secure_ref"),
          },
        },
      );

      if (res.status === 200) {
        setSnackbar({
          open: true,
          message: "Log Activity added successfully!",
          severity: "success",
        });
        if (typeof onActivityAdded === "function") {
          onActivityAdded();
        }
        handleClose();
      }
    } catch (err) {
      console.error("Error adding log activity:", err);
      setSnackbar({
        open: true,
        message: "Failed to add log activity.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Shared dropdown sx
  const dropdownSx = {
    borderRadius: "10px",
    fontSize: "14px",
    height: "42px",
    backgroundColor: "#fff",
    "& fieldset": { borderColor: "#D1D5DB" },
    "&:hover fieldset": { borderColor: "#3F95EA" },
    "&.Mui-focused fieldset": {
      borderColor: "#3F95EA",
      borderWidth: "1px",
    },
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: modalLoadingContainerSx(loading, {
            width: "520px",
            borderRadius: "16px",
            boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
            overflow: "hidden",
          }),
        }}
      >
        {/* ── Header ── */}
        <Box
          sx={{
            px: "20px",
            py: "8px",
            borderBottom: "1px solid #E8EDF2",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 600,
              color: "#0F172A",
              lineHeight: "normal",
            }}
          >
            Add Log Activity
          </Typography>
          <IconButton sx={{ p: 0.5 }} onClick={handleClose}>
            <CloseOutlinedIcon sx={{ fontSize: "20px", color: "#6B7280" }} />
          </IconButton>
        </Box>

        {/* ── Body ── */}
        <DialogContent
          sx={{
            px: "20px",
            pt: "20px",
            pb: "8px",
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            position: "relative",
          }}
        >
          {/* Regarding */}
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Regarding
            </Typography>
            <FormControl fullWidth>
              <Select
                value={regarding}
                onChange={(e) => {
                  setActivity("");
                  setActivityDesc("");
                  setRegarding(e.target.value);
                }}
                displayEmpty
                renderValue={(val) =>
                  val ? (
                    regardingOptions?.find((o) => o.job_id === val)?.title
                  ) : (
                    <span style={{ color: "#9CA3AF" }}>Jobs</span>
                  )
                }
                sx={dropdownSx}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "10px",
                      mt: "4px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      "& .MuiMenuItem-root": {
                        fontSize: "14px",
                        "&:hover": { backgroundColor: "#EBF5FF" },
                        "&.Mui-selected": { backgroundColor: "#EBF5FF" },
                      },
                    },
                  },
                }}
              >
                {regardingOptions?.length > 0 ? (
                  regardingOptions.map((opt) => (
                    <MenuItem key={opt.job_id} value={opt.job_id}>
                      {opt.title}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>No Jobs</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          {/* Activity */}
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Activity
            </Typography>
            <FormControl fullWidth disabled={!regarding}>
              <Select
                value={activity}
                onChange={(e) => {
                  setActivityDesc("");
                  setActivity(e.target.value);
                }}
                displayEmpty
                renderValue={(val) =>
                  val ? (
                    activityOptions?.find((o) => o.activity_type_id === val)
                      ?.short_description
                  ) : (
                    <span style={{ color: "#9CA3AF" }}>Activity Type</span>
                  )
                }
                sx={dropdownSx}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      borderRadius: "10px",
                      mt: "4px",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                      "& .MuiMenuItem-root": {
                        fontSize: "14px",
                        "&:hover": { backgroundColor: "#EBF5FF" },
                        "&.Mui-selected": { backgroundColor: "#EBF5FF" },
                      },
                    },
                  },
                }}
              >
                {activityOptions?.length > 0 ? (
                  activityOptions.map((opt) =>
                    opt.activity_type_id !== 400 ? (
                      <MenuItem
                        key={opt.activity_type_id}
                        value={opt.activity_type_id}
                      >
                        {opt.short_description}
                      </MenuItem>
                    ) : null,
                  )
                ) : (
                  <MenuItem disabled>No Activity Types</MenuItem>
                )}
              </Select>
            </FormControl>
          </Box>

          {/* Activity Description */}
          <Box>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                color: "#374151",
                mb: 1,
              }}
            >
              Activity Description
            </Typography>
            <TextField
              fullWidth
              placeholder="Write here.."
              multiline
              minRows={3}
              value={activityDesc}
              onChange={(e) => setActivityDesc(e.target.value)}
              disabled={!activity}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "10px",
                  fontSize: "14px",
                  backgroundColor: "#fff",
                  "& fieldset": { borderColor: "#D1D5DB" },
                  "&:hover fieldset": { borderColor: "#3F95EA" },
                  "&.Mui-focused fieldset": {
                    borderColor: "#3F95EA",
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#9CA3AF",
                  opacity: 1,
                },
              }}
            />
          </Box>
        </DialogContent>

        {/* ── Footer ── */}
        <DialogActions
          sx={{
            px: "20px",
            py: "14px",
            display: "flex",
            gap: 1.5,
            justifyContent: "flex-end",
          }}
        >
          {/* Cancel — outlined, light border */}
          <Button
            className="cancel-btn"
            variant="outlined"
            onClick={handleClose}
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              color: "#6B7280",
              borderColor: "#D1D5DB",
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              boxShadow: "none",
              "&:hover": {
                borderColor: "#9CA3AF",
                backgroundColor: "#F9FAFB",
                boxShadow: "none",
              },
            }}
          >
            Cancel
          </Button>

          {/* Save — #3F95EA enabled, #006CB7 hover, grey disabled */}
          <Button
            variant="contained"
            onClick={addLogActivity}
            disabled={isSaveDisabled}
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              borderRadius: "8px",
              px: 2.5,
              py: 0.8,
              backgroundColor: "#3F95EA",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "#006CB7",
                boxShadow: "none",
              },
              "&.Mui-disabled": {
                backgroundColor: "#E5E7EB",
                color: "#9CA3AF",
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
        <ModalLoadingOverlay open={loading} />
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
